import React from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import queryClient from '@shared/queryClient';
import Layout from '@components/Layout';
import Dashboard from './screens/dashboard/Dashboard';
import RecentActivity from './screens/dashboard/RecentActivity';
import Warehouses from './screens/warehouses/Warehouses';
import Login from './screens/login/Login';
import ProtectedRoute from '@components/ProtectedRoute';
import AdminRoute from '@components/AdminRoute';
import PermRoute from '@components/PermRoute';
import { AuthProvider } from '@shared/AuthContext';
import { CurrencyProvider } from '@shared/CurrencyContext';
import { DateFormatProvider } from '@shared/DateFormatContext';
import { ConfirmProvider } from '@shared/ConfirmContext';
import { CompanyProvider } from '@shared/CompanyContext';
import { ModulesProvider } from '@shared/ModulesContext';
import ModuleRoute from '@components/ModuleRoute';

// Import Project screens
import Projects from './screens/projects/Projects';
import ProjectDetail from './screens/projects/ProjectDetail';
import ProjectCreate from './screens/projects/ProjectCreate';
import ProjectEdit from './screens/projects/ProjectEdit';
import ProjectTrash from './screens/projects/ProjectTrash';
import TaskDetail from './screens/projects/TaskDetail';

// Import Warehouse screens
import WarehouseDetail from './screens/warehouses/WarehouseDetail';
import ProductCatalog from './screens/warehouses/ProductCatalog';
import ProductDetail from './screens/warehouses/ProductDetail';
import ProductAdd from './screens/warehouses/ProductAdd'
import ProductCategoryManagement from './screens/warehouses/ProductCategoryManagement';
import ProductTypeManagement from './screens/warehouses/ProductTypeManagement';
import BundleList from './screens/warehouses/BundleList';
import BundleDetail from './screens/warehouses/BundleDetail';
import CaseList from './screens/warehouses/CaseList';
import CaseDetail from './screens/warehouses/CaseDetail';
import SupplierManagement from './screens/warehouses/SupplierManagement';
import StockItemAdd from './screens/warehouses/StockItemAdd';
import StockInventory from './screens/warehouses/StockInventory';
import ItemUnitList from './screens/warehouses/ItemUnitList';

// Import Rental screens
import Rentals from './screens/rentals/Rentals';
import RentalDetail from './screens/rentals/RentalDetail';
import RentalSettings from './screens/rentals/RentalSettings';
import RentalAdd from './screens/rentals/RentalAdd';
import Invoices from './screens/invoices/Invoices';

// Import Customer screens
import { Customers, CustomerDetail, CustomerForm } from "./screens/customers/";

// Import Crew screens
import {Crew, CrewDetail, CrewForm, CrewStatistics, ProfessionManagement} from "./screens/crew/";

// Import Transport screens
import { Transport, TransportDetail, TransportForm, TransportStatistics, VehicleTariffTypeManagement } from './screens/transport/';

// Import Contacts screens
import { Contracts, ContractDetail, ContractStatistics } from './screens/contracts/';

// Import Notifications screen
import NotificationsPage from './screens/notifications/NotificationsPage';

// Import Chat screen
import Chat from './screens/chat/Chat';

// Import Support screens
import SupportPage from './screens/support/SupportPage';
import SupportNewTicket from './screens/support/SupportNewTicket';
import SupportTicketDetail from './screens/support/SupportTicketDetail';

// Import Accounts screens
import { Profile, UserProfileView } from './screens/accounts/';
import UserManagement from './screens/accounts/UserManagement';

import { RealtimeEventsProvider } from '@shared/useRealtimeEvents';
import { Tariffs, TariffTypes, CurrencySettings, TaxSettings, ProjectTypes, ProjectStatuses, ProjectTaskStatuses, Settings, CompanySettings, GroupManagement, ImportExport, GeneralSettings, NotificationSettings, SystemSettings, AssistantSettings } from './screens/settings/';

const router = createBrowserRouter([
  // Public routes
  { path: '/accounts/login/', element: <Login /> },
  { path: '/login', element: <Navigate to="/accounts/login/" replace /> },

  // Protected routes wrapped in Layout
  {
    element: <ProtectedRoute><Layout /></ProtectedRoute>,
    children: [
      // Dashboard
      { path: '/dashboard/*', element: <Dashboard /> },
      { path: '/dashboard/activity', element: <RecentActivity /> },
      { path: '/', element: <Navigate to="/dashboard/" replace /> },

      // Warehouse routes
      { path: '/warehouses/stocks', element: <ModuleRoute slug="warehouses"><PermRoute perm="warehouses.view_stock"><Warehouses /></PermRoute></ModuleRoute> },
      { path: '/warehouses/products', element: <ModuleRoute slug="warehouses"><PermRoute perm="warehouses.view_product"><ProductCatalog /></PermRoute></ModuleRoute> },
      { path: '/warehouses/products/add', element: <ModuleRoute slug="warehouses"><PermRoute perm="warehouses.add_product"><ProductAdd /></PermRoute></ModuleRoute> },
      { path: '/warehouses/products/:productId', element: <ModuleRoute slug="warehouses"><PermRoute perm="warehouses.view_product"><ProductDetail /></PermRoute></ModuleRoute> },
      { path: '/warehouses/products/categories', element: <ModuleRoute slug="warehouses"><PermRoute perm="warehouses.view_productcategory"><ProductCategoryManagement /></PermRoute></ModuleRoute> },
      { path: '/warehouses/products/types', element: <ModuleRoute slug="warehouses"><PermRoute perm="warehouses.view_producttype"><ProductTypeManagement /></PermRoute></ModuleRoute> },
      { path: '/warehouses/items', element: <Navigate to="/warehouses/products" replace /> },
      { path: '/warehouses/items/:itemId', element: <Navigate to="/warehouses/products" replace /> },
      { path: '/warehouses/items/add', element: <Navigate to="/warehouses/products" replace /> },
      { path: '/warehouses/items/:itemId/edit', element: <Navigate to="/warehouses/products" replace /> },
      { path: '/warehouses/items/:itemId/tariffs', element: <Navigate to="/warehouses/products" replace /> },
      { path: '/warehouses/items/categories', element: <Navigate to="/warehouses/products/categories" replace /> },
      { path: '/warehouses/items/types', element: <Navigate to="/warehouses/products/types" replace /> },
      { path: '/warehouses/items/priority', element: <Navigate to="/warehouses/products" replace /> },
      { path: '/warehouses/bundles', element: <ModuleRoute slug="warehouses"><PermRoute perm="warehouses.view_itembundle"><BundleList /></PermRoute></ModuleRoute> },
      { path: '/warehouses/bundles/:bundleId', element: <ModuleRoute slug="warehouses"><PermRoute perm="warehouses.view_itembundle"><BundleDetail /></PermRoute></ModuleRoute> },
      { path: '/warehouses/cases', element: <ModuleRoute slug="warehouses"><PermRoute perm="warehouses.view_itemcase"><CaseList /></PermRoute></ModuleRoute> },
      { path: '/warehouses/cases/:caseId', element: <ModuleRoute slug="warehouses"><PermRoute perm="warehouses.view_itemcase"><CaseDetail /></PermRoute></ModuleRoute> },
      { path: '/warehouses/suppliers', element: <ModuleRoute slug="warehouses"><PermRoute perm="warehouses.view_supplier"><SupplierManagement /></PermRoute></ModuleRoute> },
      { path: '/warehouses/stock/:warehouseId', element: <ModuleRoute slug="warehouses"><PermRoute perm="warehouses.view_stockitem"><StockInventory /></PermRoute></ModuleRoute> },
      { path: '/warehouses/stock-items/:stockItemId/units', element: <ModuleRoute slug="warehouses"><PermRoute perm="warehouses.view_itemunit"><ItemUnitList /></PermRoute></ModuleRoute> },
      { path: '/warehouses/:warehouseId/items/:itemId', element: <Navigate to="/warehouses/products" replace /> },
      { path: '/warehouses/:warehouseId/items/add', element: <ModuleRoute slug="warehouses"><PermRoute perm="warehouses.add_stockitem"><StockItemAdd /></PermRoute></ModuleRoute> },
      { path: '/warehouses/:warehouseId/items/:itemId/edit', element: <Navigate to="/warehouses/products" replace /> },
      { path: '/warehouses/:warehouseId/items/:itemId/tariffs', element: <Navigate to="/warehouses/products" replace /> },
      { path: '/warehouses/:warehouseId/items/categories', element: <Navigate to="/warehouses/products/categories" replace /> },
      { path: '/warehouses/:warehouseId/items/types', element: <Navigate to="/warehouses/products/types" replace /> },
      { path: '/warehouses/:warehouseId/items/priority', element: <Navigate to="/warehouses/products" replace /> },

      // Customer routes
      { path: '/customers/*', element: <PermRoute perm="customers.view_customer"><Customers /></PermRoute> },
      { path: '/customers/customer/:customerId', element: <PermRoute perm="customers.view_customer"><CustomerDetail /></PermRoute> },
      { path: '/customers/customer/:customerId/edit', element: <PermRoute perm="customers.change_customer"><CustomerForm /></PermRoute> },
      { path: '/customers/create', element: <PermRoute perm="customers.add_customer"><CustomerForm /></PermRoute> },

      // Contracts routes
      { path: '/contracts', element: <ModuleRoute slug="rentals"><PermRoute perm="contracts.view_contract"><Contracts /></PermRoute></ModuleRoute> },
      { path: '/contracts/contract/:contractId', element: <ModuleRoute slug="rentals"><PermRoute perm="contracts.view_contract"><ContractDetail /></PermRoute></ModuleRoute> },
      { path: '/contracts/statistics', element: <ModuleRoute slug="rentals"><PermRoute perm="contracts.view_contract"><ContractStatistics /></PermRoute></ModuleRoute> },

      // Project routes
      { path: '/projects', element: <ModuleRoute slug="projects"><PermRoute perm="projects.view_project"><Projects /></PermRoute></ModuleRoute> },
      { path: '/projects/trash', element: <ModuleRoute slug="projects"><PermRoute perm="projects.delete_project"><ProjectTrash /></PermRoute></ModuleRoute> },
      { path: '/projects/create', element: <ModuleRoute slug="projects"><PermRoute perm="projects.add_project"><ProjectCreate /></PermRoute></ModuleRoute> },
      { path: '/projects/edit/:projectId', element: <ModuleRoute slug="projects"><PermRoute perm="projects.change_project"><ProjectEdit /></PermRoute></ModuleRoute> },
      { path: '/projects/detail/:projectId', element: <ModuleRoute slug="projects"><PermRoute perm="projects.view_project"><ProjectDetail /></PermRoute></ModuleRoute> },
      { path: '/projects/detail/:projectId/task/detail/:taskId', element: <ModuleRoute slug="projects"><PermRoute perm="projects.view_project"><TaskDetail /></PermRoute></ModuleRoute> },
      { path: '/projects/detail/:projectId/files/*', element: <ModuleRoute slug="projects"><PermRoute perm="projects.view_project"><ProjectDetail /></PermRoute></ModuleRoute> },
      { path: '/projects/detail/:projectId/notes/*', element: <ModuleRoute slug="projects"><PermRoute perm="projects.view_project"><ProjectDetail /></PermRoute></ModuleRoute> },
      { path: '/projects/detail/:projectId/staff/*', element: <ModuleRoute slug="projects"><PermRoute perm="projects.view_project"><ProjectDetail /></PermRoute></ModuleRoute> },
      { path: '/projects/detail/:projectId/vehicles/*', element: <ModuleRoute slug="projects"><PermRoute perm="projects.view_project"><ProjectDetail /></PermRoute></ModuleRoute> },
      { path: '/projects/detail/:projectId/offers/*', element: <ModuleRoute slug="projects"><PermRoute perm="projects.view_project"><ProjectDetail /></PermRoute></ModuleRoute> },
      { path: '/projects/detail/:projectId/items/*', element: <ModuleRoute slug="projects"><PermRoute perm="projects.view_project"><ProjectDetail /></PermRoute></ModuleRoute> },
      { path: '/projects/detail/:projectId/cases/*', element: <ModuleRoute slug="projects"><PermRoute perm="projects.view_project"><ProjectDetail /></PermRoute></ModuleRoute> },
      { path: '/projects/detail/:projectId/tasks/*', element: <ModuleRoute slug="projects"><PermRoute perm="projects.view_project"><ProjectDetail /></PermRoute></ModuleRoute> },
      { path: '/projects/detail/:projectId/info/*', element: <ModuleRoute slug="projects"><PermRoute perm="projects.view_project"><ProjectDetail /></PermRoute></ModuleRoute> },
      { path: '/projects/tasks/*', element: <ModuleRoute slug="projects"><PermRoute perm="projects.view_project"><ProjectDetail /></PermRoute></ModuleRoute> },

      // Invoices
      { path: '/invoices/*', element: <ModuleRoute slug="invoices"><PermRoute perm="invoices.view_invoice"><Invoices /></PermRoute></ModuleRoute> },

      // Rental routes
      { path: '/rentals', element: <ModuleRoute slug="rentals"><PermRoute perm="rentals.view_rental"><Rentals /></PermRoute></ModuleRoute> },
      { path: '/rentals/rental/:id', element: <ModuleRoute slug="rentals"><PermRoute perm="rentals.view_rental"><RentalDetail /></PermRoute></ModuleRoute> },
      { path: '/rentals/add', element: <ModuleRoute slug="rentals"><PermRoute perm="rentals.add_rental"><RentalAdd /></PermRoute></ModuleRoute> },
      { path: '/rentals/rental/:id/settings/*', element: <ModuleRoute slug="rentals"><PermRoute perm="rentals.change_rental"><RentalSettings /></PermRoute></ModuleRoute> },
      { path: '/rentals/rental/:id/settings/general/*', element: <ModuleRoute slug="rentals"><PermRoute perm="rentals.change_rental"><RentalSettings /></PermRoute></ModuleRoute> },
      { path: '/rentals/rental/:id/settings/customer/*', element: <ModuleRoute slug="rentals"><PermRoute perm="rentals.change_rental"><RentalSettings /></PermRoute></ModuleRoute> },
      { path: '/rentals/rental/:id/settings/items/*', element: <ModuleRoute slug="rentals"><PermRoute perm="rentals.change_rental"><RentalSettings /></PermRoute></ModuleRoute> },
      { path: '/rentals/rental/:id/settings/prices/*', element: <ModuleRoute slug="rentals"><PermRoute perm="rentals.change_rental"><RentalSettings /></PermRoute></ModuleRoute> },
      { path: '/rentals/rental/:id/settings/history/*', element: <ModuleRoute slug="rentals"><PermRoute perm="rentals.change_rental"><RentalSettings /></PermRoute></ModuleRoute> },

      // Crew routes
      { path: '/crew/*', element: <PermRoute perm="crew_members.view_crewmember"><Crew /></PermRoute> },
      { path: '/crew/:crewId', element: <PermRoute perm="crew_members.view_crewmember"><CrewDetail /></PermRoute> },
      { path: '/crew/:crewId/edit', element: <PermRoute perm="crew_members.change_crewmember"><CrewForm /></PermRoute> },
      { path: '/crew/create', element: <PermRoute perm="crew_members.add_crewmember"><CrewForm /></PermRoute> },
      { path: '/crew/statistics', element: <PermRoute perm="crew_members.view_crewmember"><CrewStatistics /></PermRoute> },
      { path: '/crew/professions', element: <PermRoute perm="crew_members.view_crewmember"><ProfessionManagement /></PermRoute> },

      // Transport routes
      { path: '/transport/*', element: <PermRoute perm="transport.view_vehicle"><Transport /></PermRoute> },
      { path: '/transport/:transportId', element: <PermRoute perm="transport.view_vehicle"><TransportDetail /></PermRoute> },
      { path: '/transport/:transportId/edit', element: <PermRoute perm="transport.change_vehicle"><TransportForm /></PermRoute> },
      { path: '/transport/statistics', element: <PermRoute perm="transport.view_vehicle"><TransportStatistics /></PermRoute> },
      { path: '/transport/tariff-types', element: <PermRoute perm="transport.view_vehicle"><VehicleTariffTypeManagement /></PermRoute> },

      // Notifications
      { path: '/notifications', element: <NotificationsPage /> },

      // AI Assistant (legacy redirect)
      { path: '/assistant/*', element: <Navigate to="/chat" replace /> },

      // Chat
      { path: '/chat', element: <ModuleRoute slug="ai-assistant"><Chat /></ModuleRoute> },
      { path: '/chat/:roomId', element: <ModuleRoute slug="ai-assistant"><Chat /></ModuleRoute> },

      // Support
      { path: '/support', element: <PermRoute perm="support.view_ticket"><SupportPage /></PermRoute> },
      { path: '/support/new', element: <PermRoute perm="support.add_ticket"><SupportNewTicket /></PermRoute> },
      { path: '/support/:reference', element: <PermRoute perm="support.view_ticket"><SupportTicketDetail /></PermRoute> },

      // Accounts
      { path: '/accounts', element: <AdminRoute><UserManagement /></AdminRoute> },
      { path: '/accounts/profile', element: <Profile /> },
      { path: '/accounts/profile/:userId', element: <UserProfileView /> },

      // Settings (admin only)
      { path: '/settings', element: <AdminRoute><Settings /></AdminRoute> },
      { path: '/settings/company', element: <AdminRoute><CompanySettings /></AdminRoute> },
      { path: '/settings/assistant', element: <AdminRoute><AssistantSettings /></AdminRoute> },
      { path: '/settings/tariff-types', element: <AdminRoute><TariffTypes /></AdminRoute> },
      { path: '/settings/tariffs', element: <AdminRoute><Tariffs /></AdminRoute> },
      { path: '/settings/currency', element: <AdminRoute><CurrencySettings /></AdminRoute> },
      { path: '/settings/tax', element: <AdminRoute><TaxSettings /></AdminRoute> },
      { path: '/settings/project-types', element: <AdminRoute><ProjectTypes /></AdminRoute> },
      { path: '/settings/project-statuses', element: <AdminRoute><ProjectStatuses /></AdminRoute> },
      { path: '/settings/project-task-statuses', element: <AdminRoute><ProjectTaskStatuses /></AdminRoute> },
      { path: '/settings/groups', element: <AdminRoute><GroupManagement /></AdminRoute> },
      { path: '/settings/import-export', element: <AdminRoute><ImportExport /></AdminRoute> },
      { path: '/settings/general', element: <AdminRoute><GeneralSettings /></AdminRoute> },
      { path: '/settings/notifications', element: <AdminRoute><NotificationSettings /></AdminRoute> },
      { path: '/settings/system', element: <AdminRoute><SystemSettings /></AdminRoute> },
      { path: '/settings/*', element: <AdminRoute><Settings /></AdminRoute> },
    ],
  },

  // API routes — pass through to backend (no redirect)
  { path: '/api/*', element: null },

  // Catch-all redirect
  { path: '*', element: <Navigate to="/dashboard/" replace /> },
]);

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
      <AuthProvider>
        <CompanyProvider>
          <CurrencyProvider>
            <DateFormatProvider>
              <ConfirmProvider>
                <RealtimeEventsProvider>
                  <ModulesProvider>
                    <RouterProvider router={router} />
                  </ModulesProvider>
                </RealtimeEventsProvider>
              </ConfirmProvider>
            </DateFormatProvider>
          </CurrencyProvider>
        </CompanyProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
